import { lazy, Suspense, useCallback, useEffect, useState, useMemo } from 'react'
import { useLocation, useTiles } from '../hooks'
import { LocationInput, StationDetailSheet, SortDropdown } from '../components'
import { DraggableSheet, type SheetPosition } from '../components/DraggableSheet'
import { CompactStationCard } from '../components/CompactStationCard'
import { Loader2 } from 'lucide-react'
import type { ChargepointSummaryWithDistance, SortOption, SpeedTier } from '@fuelfinder/shared'
import { calculateDistanceMiles } from '@fuelfinder/shared'
import { readUrlState, writeUrlState } from '../lib/urlState'

// Lazy load the MapView component (includes Leaflet ~40KB gzipped)
const MapView = lazy(() =>
  import('../components/MapView').then((m) => ({ default: m.MapView }))
)

interface HybridPageProps {
  speedTier: SpeedTier | 'all'
  sortBy: SortOption
  onSortChange: (sort: SortOption) => void
  refreshTrigger?: number
  searchPostcode?: string | null
  searchCoords?: { lat: number; lng: number } | null
  /** Active search term (place name or postcode), carried into the shared URL */
  searchTerm?: string | null
}

/** Height of fixed header elements (header + location search + speed tier toggle) */
const FIXED_HEADER_HEIGHT = 160

/**
 * At or below this zoom the 9-tile fetch (~15km) covers a fraction of the
 * viewport, so we switch to loading the full national dataset instead.
 */
const NATIONAL_VIEW_ZOOM = 11

/** Max list entries rendered in the sheet (data may hold the whole country) */
const MAX_LIST_ITEMS = 100

/** Sheet snap point percentages (must match DraggableSheet) */
const SHEET_SNAP_POINTS: Record<SheetPosition, number> = {
  minimized: 0.15,
  half: 0.55,
  maximized: 0.90,
}

/**
 * Loading spinner for map lazy load
 */
function MapLoadingSpinner() {
  return (
    <div className="h-full w-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
      <div className="flex flex-col items-center gap-3">
        <Loader2 size={32} className="text-brand-500 animate-spin" />
        <p className="text-slate-600 dark:text-slate-300 text-sm">Loading map...</p>
      </div>
    </div>
  )
}

/**
 * Loading overlay for when chargepoints are still being fetched
 */
function ChargepointsLoadingOverlay() {
  return (
    <div className="absolute inset-0 bg-slate-900/30 flex items-center justify-center z-[500]">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg px-6 py-4 flex items-center gap-3">
        <Loader2 size={20} className="text-brand-500 animate-spin" />
        <span className="text-slate-700 dark:text-slate-200 text-sm font-medium">
          Loading chargepoints...
        </span>
      </div>
    </div>
  )
}

/**
 * Sort chargepoints based on sort option
 */
function sortChargepoints(
  chargepoints: ChargepointSummaryWithDistance[],
  sortBy: SortOption,
): ChargepointSummaryWithDistance[] {
  const sorted = [...chargepoints]

  switch (sortBy) {
    case 'nearest':
      sorted.sort((a, b) => a.distanceMiles - b.distanceMiles)
      break
    case 'fastest':
      sorted.sort((a, b) => b.maxPowerKW - a.maxPowerKW)
      break
    case 'most-connectors':
      sorted.sort((a, b) => b.totalConnectors - a.totalConnectors)
      break
  }

  return sorted
}

/**
 * Hybrid page combining map view with draggable chargepoint list.
 * Single source of truth for location and chargepoint data.
 */
export function HybridPage({
  speedTier,
  sortBy,
  onSortChange,
  refreshTrigger,
  searchPostcode,
  searchCoords,
  searchTerm,
}: HybridPageProps) {
  const {
    location,
    gpsLocation,
    isLoading: locationLoading,
    error: locationError,
    setPostcode,
    setCoordinates,
    resetToGps,
  } = useLocation()

  // Zoom from a shared URL / refreshed view. The location itself is seeded
  // from the URL inside useLocation, so the map frames it with this zoom.
  const [bootZoom] = useState<number | null>(() => readUrlState().z ?? null)

  // Handle search postcode from header
  useEffect(() => {
    if (searchPostcode) {
      setPostcode(searchPostcode)
    }
  }, [searchPostcode, setPostcode])

  // Handle search coordinates from header
  useEffect(() => {
    if (searchCoords) {
      setCoordinates(searchCoords.lat, searchCoords.lng)
    }
  }, [searchCoords, setCoordinates])

  const { chargepoints, isLoading: tilesLoading, error: tilesError, refetch, fetchAroundPoint, loadNationalDataset } = useTiles(location)
  // Where the user has panned the map to (null = following the location);
  // the list anchors here so it reflects what the map is showing
  const [mapAnchor, setMapAnchor] = useState<{ lat: number; lng: number } | null>(null)
  const [selectedChargepoint, setSelectedChargepoint] = useState<ChargepointSummaryWithDistance | null>(null)
  const [sheetPosition, setSheetPosition] = useState<SheetPosition>('half')
  const [availableHeight, setAvailableHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight - FIXED_HEADER_HEIGHT : 600
  )

  // Filter by speed tier
  const filteredChargepoints = useMemo(() => {
    if (speedTier === 'all') return chargepoints
    return chargepoints.filter((cp) => cp.speedTier === speedTier)
  }, [chargepoints, speedTier])

  // The list anchors to wherever the map is looking: the panned-to centre,
  // falling back to the active location. Reset to the location whenever it
  // changes (search, GPS, recenter).
  const anchor = useMemo(
    () => mapAnchor ?? (location ? { lat: location.lat, lng: location.lng } : null),
    [mapAnchor, location?.lat, location?.lng] // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    setMapAnchor(null)
  }, [location?.lat, location?.lng, location?.timestamp])

  // The URL is written from the map's actual settled view (see handleMapMove),
  // which fires for both user pans and the app's own recenter/fit after a
  // search — so the shared/refreshed view is exactly what's on screen.

  // List entries: nearest to the anchor first (distances recomputed against
  // it), capped, then sorted by the chosen option.
  const listChargepoints = useMemo(() => {
    if (!anchor) return []
    const nearest = filteredChargepoints
      .map((cp) => ({
        ...cp,
        distanceMiles: calculateDistanceMiles(anchor.lat, anchor.lng, cp.lat, cp.lng),
      }))
      .sort((a, b) => a.distanceMiles - b.distanceMiles)
      .slice(0, MAX_LIST_ITEMS)
    return sortChargepoints(nearest, sortBy)
  }, [filteredChargepoints, sortBy, anchor])

  // Update available height on resize
  useEffect(() => {
    const updateHeight = () => {
      setAvailableHeight(window.innerHeight - FIXED_HEADER_HEIGHT)
    }
    window.addEventListener('resize', updateHeight)
    return () => window.removeEventListener('resize', updateHeight)
  }, [])

  // Trigger refetch when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      refetch()
    }
  }, [refreshTrigger, refetch])

  // The app framed the map (recenter / nearest-chargers fit / URL restore):
  // record the settled centre + zoom (keeping the search term) so a refresh or
  // share reproduces exactly what's on screen.
  const handleFramed = useCallback(
    (lat: number, lng: number, zoom: number) => {
      writeUrlState({ lat, lng, z: zoom, ...(searchTerm ? { q: searchTerm } : {}) })
    },
    [searchTerm]
  )

  // A genuine user pan/zoom: fetch data for the new view, re-anchor the list to
  // the map centre, and record the view (dropping the search term — it no
  // longer describes where they've panned to).
  const handleMapMove = useCallback(
    (lat: number, lng: number, zoom: number) => {
      if (zoom <= NATIONAL_VIEW_ZOOM) {
        loadNationalDataset()
      } else {
        fetchAroundPoint(lat, lng)
      }
      setMapAnchor({ lat, lng })
      writeUrlState({ lat, lng, z: zoom })
    },
    [loadNationalDataset, fetchAroundPoint]
  )

  // Handle chargepoint marker click on map
  const handleChargepointClick = (chargepointId: string) => {
    const cp = chargepoints.find((c) => c.id === chargepointId)
    if (cp) {
      setSelectedChargepoint(cp)
    }
  }

  // Handle chargepoint tap in list
  const handleListChargepointClick = (cp: ChargepointSummaryWithDistance) => {
    setSelectedChargepoint(cp)
  }

  // Show postcode input if no location
  if (!location && !locationLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        <LocationInput onSubmit={setPostcode} isLoading={locationLoading} />
        {locationError && (
          <p className="text-center text-red-600 dark:text-red-400 text-sm px-4 -mt-4">
            {locationError}
          </p>
        )}
      </div>
    )
  }

  // Show loading state while getting location
  if (locationLoading && !location) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="animate-pulse w-16 h-16 bg-slate-200 dark:bg-slate-800 rounded-full mb-4" />
          <p className="text-slate-600 dark:text-slate-300">Getting your location...</p>
        </div>
      </div>
    )
  }

  // Calculate sheet height for map centering offset
  const sheetHeight = availableHeight * SHEET_SNAP_POINTS[sheetPosition]

  const countLabel =
    filteredChargepoints.length > MAX_LIST_ITEMS
      ? `Nearest ${MAX_LIST_ITEMS} chargepoints`
      : filteredChargepoints.length === 1
        ? '1 chargepoint nearby'
        : `${filteredChargepoints.length} chargepoints nearby`

  // Sheet header content with chargepoint count and sort dropdown
  const sheetHeaderContent = (
    <div className="flex items-center justify-between w-full">
      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
        {countLabel}
      </span>
      <SortDropdown value={sortBy} onChange={onSortChange} />
    </div>
  )

  return (
    <>
      {/* Fixed container for map and sheet */}
      <div
        className="fixed left-0 right-0 bottom-0"
        style={{ top: FIXED_HEADER_HEIGHT }}
      >
        {/* Map fills the entire container */}
        <div className="hybrid-map-container">
          {/* Error banner */}
          {tilesError && (
            <div className="absolute top-4 left-4 right-4 z-[500]">
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg px-4 py-2 shadow-lg">
                <p className="text-red-800 dark:text-red-200 text-sm">{tilesError}</p>
              </div>
            </div>
          )}

          {/* Loading overlay while fetching chargepoints */}
          {tilesLoading && chargepoints.length === 0 && <ChargepointsLoadingOverlay />}

          {/* Lazy loaded map */}
          <Suspense fallback={<MapLoadingSpinner />}>
            <MapView
              chargepoints={filteredChargepoints}
              userLocation={location ? { lat: location.lat, lng: location.lng, at: location.timestamp } : null}
              gpsLocation={gpsLocation ? { lat: gpsLocation.lat, lng: gpsLocation.lng } : null}
              onChargepointClick={handleChargepointClick}
              onRecenter={resetToGps}
              onMapMove={handleMapMove}
              onFramed={handleFramed}
              initialZoom={bootZoom}
              bottomSheetHeight={sheetHeight}
            />
          </Suspense>
        </div>

        {/* Draggable sheet with chargepoint list */}
        <DraggableSheet
          position={sheetPosition}
          onPositionChange={setSheetPosition}
          availableHeight={availableHeight}
          headerContent={sheetHeaderContent}
        >
          {listChargepoints.length === 0 && !tilesLoading ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <p className="text-slate-500 dark:text-slate-400 text-center">
                No chargepoints found in your area
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {listChargepoints.map((cp) => (
                <CompactStationCard
                  key={cp.id}
                  chargepoint={cp}
                  onClick={() => handleListChargepointClick(cp)}
                />
              ))}
            </div>
          )}
        </DraggableSheet>
      </div>

      {/* Chargepoint detail sheet */}
      {selectedChargepoint && (
        <StationDetailSheet
          chargepoint={selectedChargepoint}
          onClose={() => setSelectedChargepoint(null)}
        />
      )}
    </>
  )
}
